import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./OurProcessSection.css";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";

export default function OurProcessSection({ currentLanguage = "en" }) {
  const isFrench = currentLanguage === "fr";

  // ================= ENGLISH DATA =================
  const dataEN = {
    subtitle: "OUR APPROCH",
    heading: "HOW WE DELIVER RESULTS",
    description:
      "At Pestel & Partners, our process blends analytical rigor with financial expertise. Each engagement follows a structured approach designed to ensure accuracy, regulatory compliance, and measurable business outcomes.",
    steps: [
      {
        icon: "assets/icons/Discovery.png",
        title: "Diagnostic Evaluation",
        desc: "We start by conducting a thorough assessment of financial statements, controls, and governance structures to identify risks, inefficiencies, and untapped opportunities.",
        className: "icon-discovery",
      },
      {
        icon: "assets/icons/Strategy.png",
        title: "Financial Planning",
        desc: "Our experts develop tailored financial roadmaps —integrating audit findings, market analysis, and compliance frameworks to align with organizational goals.",
        className: "icon-strategy",
      },
      {
        icon: "assets/icons/Implementation.png",
        title: "Risk Alignment",
        desc: "We ensure every recommendation meets local and international standards, mitigating exposure across tax, financial, and operational areas.",
        className: "icon-implementation",
      },
      {
        icon: "assets/icons/Support.png",
        title: "Advisory Execution",
        desc: "Our multidisciplinary teams execute agreed strategies, assist in restructuring, and provide continuous advisory support throughout the transformation journey.",
        className: "icon-support",
      },
      {
        icon: "assets/icons/Growthh.png",
        title: "Performance Review",
        desc: "We track outcomes through financial KPIs, performance metrics, and compliance indicators—ensuring sustained value creation and transparency over time.",
        className: "icon-growth",
      },
    ],
  };

  // ================= FRENCH DATA =================
  const dataFR = {
    subtitle: "NOTRE APPROCHE",
    heading: "COMMENT NOUS OBTENONS DES RÉSULTATS",
    description:
      "Chez Pestel & Partners, notre méthodologie allie rigueur analytique et expertise financière. Chaque mission suit une approche structurée visant à garantir la conformité réglementaire, la précision et des résultats mesurables.",
    steps: [
      {
        icon: "assets/icons/Discovery.png",
        title: "Évaluation diagnostique",
        desc: "Nous commençons par une analyse approfondie des états financiers, des contrôles et des structures de gouvernance afin d’identifier les risques, les inefficacités et les opportunités inexploitées.",
        className: "icon-discovery",
      },
      {
        icon: "assets/icons/Strategy.png",
        title: "Planification financière",
        desc: "Nos experts élaborent des feuilles de route financières sur mesure, intégrant les résultats d’audit, l’analyse du marché et les cadres de conformité.",
        className: "icon-strategy",
      },
      {
        icon: "assets/icons/Implementation.png",
        title: "Alignement des risques",
        desc: "Nous veillons à ce que chaque recommandation respecte les normes locales et internationales, réduisant ainsi les risques fiscaux, financiers et opérationnels.",
        className: "icon-implementation",
      },
      {
        icon: "assets/icons/Support.png",
        title: "Exécution du conseil",
        desc: "Nos équipes pluridisciplinaires mettent en œuvre les stratégies définies, accompagnent les restructurations et assurent un suivi continu.",
        className: "icon-support",
      },
      {
        icon: "assets/icons/Growthh.png",
        title: "Revue de performance",
        desc: "Nous mesurons les résultats à l’aide d’indicateurs financiers, de performance et de conformité afin d’assurer une création de valeur durable.",
        className: "icon-growth",
      },
    ],
  };

  // ================= SELECT LANGUAGE =================
  const data = isFrench ? dataFR : dataEN;
  const { subtitle, heading, description, steps } = data;

  // ICON COLOR ALTERNATE (not background)
  const iconColors = ["#143C8C", "#FBBF24"]; // blue, yellow

  return (
    <section className="ourprocess-section section">
      <div className="text-center">
        <div className="d-flex align-items-center justify-content-center mb-2">
          <div className="about-line me-3"></div>
          <p className="service-subtitle mb-0">{subtitle}</p>
        </div>

        <h2 className="client-heading">{heading}</h2>

        <p className="process-description mx-auto mb-5">
          {description}
        </p>
      </div>

      {/* 🔵 DESKTOP ICON ROW */}
      <div className="process-icons-row">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <div
              className={`process-icon-wrapper ${
                index % 2 === 0 ? "blue-gradient" : "yellow-gradient"
              }`}
            >
              <img
                src={step.icon}
                alt={step.title}
                className={`process-icon-img ${step.className}`}
              />
            </div>

            {index < steps.length - 1 && (
              <div className="circle-connector">
                <div className="circle-line">
                  <MdOutlineKeyboardArrowRight
                    className="circle-arrow"
                    style={{ color: iconColors[index % 2] }}
                  />
                </div>
                <div className="circle-line"></div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* TEXT ROW */}
      <div className="row justify-content-between text-row">
        {steps.map((step, index) => (
          <div className="col-6 col-md text-center process-step" key={index}>
            <h5 className="process-step-title mt-3">{step.title}</h5>
            <p className="process-step-desc">{step.desc}</p>
          </div>
        ))}
      </div>

      {/* ===== MOBILE VIEW ===== */}
      <div className="process-mobile">
        {steps.map((step, index) => (
          <div className="mobile-row" key={index}>
            <div className="mobile-left">
              <div
                className={`mobile-icon ${
                  index % 2 === 0 ? "blue-gradient" : "yellow-gradient"
                }`}
              >
                <img src={step.icon} alt={step.title} />
              </div>

              {index < steps.length - 1 && (
                <div className="mobile-line-wrapper">
                  <div className="mobile-line"></div>
                  <MdOutlineKeyboardArrowRight className="mobile-arrow" />
                  <div className="mobile-line"></div>
                </div>
              )}
            </div>

            <div className="mobile-right">
              <h5 className="mobile-title">{step.title}</h5>
              <p className="mobile-desc">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
